<div class="flex-table-page">
<div class="px-6 pt-6 flex-table-header">
  <h2 class="text-2xl font-semibold text-gray-800">
    {{ "e_invoice.account.title" | translate }}
  </h2>
</div>
<div class="px-6 py-4 flex flex-col flex-1 min-h-0">
  <!-- Search Form -->
  <app-search-form
    data-testid="e-invoice-account-search-form"
    [searchFormat]="searchFormConfig()"
    [presetValues]="presetValues()"
    (formSearch)="onSearch($event)"
    (formReset)="onReset()"
  >
    <div slot="actions">
      <app-status-filter
        data-testid="e-invoice-account-status-filter"
        [primaryButton]="primaryButton"
        [secondaryButton]="secondaryButton"
        (primaryButtonClick)="onAdd()"
        (secondaryButtonClick)="onColumnConfigClick($event)"
      ></app-status-filter>
    </div>
  </app-search-form>

  <!-- Empty states -->
  @if (isEmpty()) {
    <div
      data-testid="e-invoice-account-empty"
      class="flex flex-1 flex-col items-center justify-start gap-3 pt-12 text-center"
    >
      @if (hasActiveFilters()) {
        <div class="text-base font-medium text-gray-700">
          {{ "e_invoice.account.empty.no_results_title" | translate }}
        </div>
        <div class="text-sm text-gray-500">
          {{ "e_invoice.account.empty.no_results_hint" | translate }}
        </div>
        <p-button
          data-testid="e-invoice-account-empty-clear"
          [label]="'e_invoice.account.empty.clear_filters' | translate"
          severity="secondary"
          [outlined]="true"
          (onClick)="onClearFilters()"
        />
      } @else {
        <div class="text-base font-medium text-gray-700">
          {{ "e_invoice.account.empty.none_title" | translate }}
        </div>
        <div class="text-sm text-gray-500">
          {{ "e_invoice.account.empty.none_hint" | translate }}
        </div>
        @if (canCreate()) {
          <p-button
            data-testid="e-invoice-account-empty-create"
            [label]="'common.actions.new' | translate"
            icon="pi pi-plus"
            severity="info"
            (onClick)="onAdd()"
          />
        }
      }
    </div>
  }

  <!-- Data Table -->
  @if (!isEmpty()) {
  <div class="flex-table-section">
    <advanced-data-table
      data-testid="e-invoice-account-table"
      #advancedTable
      [dataSource]="dataSource()"
      [defaultColumns]="defaultColumns"
      [flexibleColumns]="flexibleColumns"
      [rows]="rows"
      [first]="first"
      [totalRecords]="totalRecords()"
      [loading]="loading()"
      [showInternalColumnConfigButton]="false"
      [rowClass]="getRowClass"
      scrollHeight="flex"
      (pageChange)="onPageChange($event)"
    >
    <!-- Custom Template for Status Columns -->
    <ng-template
      #customTemplate
      let-value
      let-column="column"
      let-rowData="rowData"
    >
      @if (column?.prop === "status") {
        <div class="flex items-center justify-center gap-1 w-full">
          <p-tag
            [attr.data-testid]="'e-invoice-account-status-tag-' + rowData.id"
            [value]="getStatusDisplayText(value)"
            [severity]="getStatusSeverity(value)"
            styleClass="status-tag"
          />
          @if (rowData.quotaExhausted) {
            <p-tag
              [attr.data-testid]="'e-invoice-account-health-tag-' + rowData.id"
              [value]="'e_invoice.account.health.exhausted_short' | translate"
              severity="danger"
              icon="pi pi-exclamation-triangle"
              styleClass="status-tag"
            />
          }
        </div>
      }

      @if (column?.prop === "templateSeries") {
        <div class="w-full truncate">
          {{ rowData.invoiceTemplate || "-" }} / {{ rowData.invoiceSeries || "-" }}
        </div>
      }

      @if (column?.prop === "pendingFailedCount") {
        <div class="flex items-center justify-center gap-2 w-full">
          @if (value > 0) {
            <p-tag
              [attr.data-testid]="'e-invoice-account-pending-tag-' + rowData.id"
              [value]="
                'e_invoice.account.table.pending_tag'
                  | translate: { count: value }
              "
              severity="warn"
              styleClass="status-tag pending-tag"
            />
            @if (canUpdate()) {
              <button
                [attr.data-testid]="'e-invoice-account-action-retry-' + rowData.id"
                type="button"
                (click)="onRetryPending(rowData)"
                [disabled]="retryingAccountIds().has(rowData.id)"
                class="action-icon"
                [pTooltip]="'e_invoice.account.actions.retry_pending_short' | translate"
                tooltipPosition="top"
                [attr.aria-label]="'e_invoice.account.actions.retry_pending_short' | translate"
              >
                <i
                  class="pi pi-refresh"
                  [class.pi-spin]="retryingAccountIds().has(rowData.id)"
                  aria-hidden="true"
                ></i>
              </button>
            }
          } @else {
            <span class="text-gray-400">-</span>
          }
        </div>
      }

      @if (column?.prop === "exhaustedAt" || column?.prop === "lastIssuedAt") {
        <div class="w-full">
          {{ value ? (value | date: "dd/MM/yyyy HH:mm") : "-" }}
        </div>
      }

      @if (column?.prop === "companyName") {
        <div class="w-full">
          {{ value || ("e_invoice.account.table.company_na" | translate) }}
        </div>
      }
    </ng-template>

    <!-- Custom Actions Template -->
    <ng-template #actionsTemplate let-rowData>
      <div class="flex items-center justify-center gap-1">
        @if (canView()) {
          <button
            [attr.data-testid]="'e-invoice-account-action-view-' + rowData.id"
            type="button"
            (click)="onView(rowData)"
            class="action-icon"
            [pTooltip]="'common.actions.view' | translate"
            tooltipPosition="top"
            [attr.aria-label]="'common.actions.view' | translate"
          >
            <i class="pi pi-eye" aria-hidden="true"></i>
          </button>
        }
        @if (canUpdate()) {
          <button
            [attr.data-testid]="'e-invoice-account-action-edit-' + rowData.id"
            type="button"
            (click)="onEdit(rowData)"
            class="action-icon"
            [pTooltip]="'common.actions.edit' | translate"
            tooltipPosition="top"
            [attr.aria-label]="'common.actions.edit' | translate"
          >
            <i class="pi pi-pencil" aria-hidden="true"></i>
          </button>
        }
        @if (hasOthersActions()) {
          <button
            [attr.data-testid]="'e-invoice-account-action-others-' + rowData.id"
            type="button"
            aria-haspopup="menu"
            [attr.aria-expanded]="openMenuAccountId() === rowData.id"
            (click)="toggleOthersMenu($event, rowData)"
            class="action-icon"
            [pTooltip]="'common.actions.others' | translate"
            tooltipPosition="top"
            [attr.aria-label]="'common.actions.others' | translate"
          >
            <i class="pi pi-ellipsis-v" aria-hidden="true"></i>
          </button>
        }
      </div>
    </ng-template>
    </advanced-data-table>
  </div>
  }
</div>
</div>

<p-menu
  #actionMenu
  [popup]="true"
  [model]="currentMenuModel()"
  appendTo="body"
  [baseZIndex]="2100"
  styleClass="actions-menu text-[0.75rem]"
  (onHide)="onMenuHide()"
></p-menu>

<!-- Toast Messages -->
<p-toast data-testid="e-invoice-account-toast"></p-toast>

<!-- Confirmation Dialog -->
<app-confirmation-dialog
  data-testid="e-invoice-account-confirmation-dialog"
  [(visible)]="showConfirmationDialog"
  [title]="confirmationTitle()"
  [message]="confirmationMessage()"
  [buttons]="confirmationButtons()"
  (emitOnClick)="onConfirmationDialogAction($event)"
></app-confirmation-dialog>
